import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostBinding,
  inject,
  InjectionToken,
  Signal,
  signal,
  ViewEncapsulation,
  WritableSignal
} from "@angular/core";
import { SidebarContentComponent } from "@core/components/ui/sidebar/components/content/sidebar-content.component";
import { SidebarHeaderComponent } from "@core/components/ui/sidebar/components/header/sidebar-header.component";
import { HttpClient } from "@angular/common/http";
import { ApiSidebarItem, SidebarItem } from "@core/types/sidebar.type";
import { CurrentContextService } from "@core/services/current-context.service";
import { CurrentEntity } from "@core/types/navigation.type";
import { map, Observable, of, throttleTime } from "rxjs";
import { FaIconLibrary } from "@fortawesome/angular-fontawesome";
import { SIDEBAR_ICONS } from "@core/components/ui/sidebar/constants/icon.constant";
import { apiToDomain } from "@core/utils/sidebar.utils";
import { KeycloakService } from "@ogs/ngx-keycloak";
import { NavigationService } from "@core/services/navigation/navigation.service";
import { LocaleConfig, TranslationStoreService } from "@ogs-gmbh/ngx-translate";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { SidebarService } from "@core/services/sidebar.service";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";

export const SIDEBAR_OFFSET_REF: InjectionToken<ElementRef> = new InjectionToken<ElementRef>('OffsetRef');
@Component({
  selector: "app-core-ui-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrl: "sidebar.component.scss",
  standalone: true,
  providers: [
    { provide: SIDEBAR_OFFSET_REF, useExisting: ElementRef }
  ],
  imports: [
    SidebarContentComponent,
    SidebarHeaderComponent,
    IconModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.Emulated
})
export class SidebarComponent {
  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  @HostBinding('class.collapsed')
  get isCollapsed (): boolean {
    return this._sidebarService.collapsed();
  }

  elementRef: ElementRef = inject(ElementRef);

  items: WritableSignal<SidebarItem[] | null> = signal(null);

  private readonly contextService: CurrentContextService = inject(CurrentContextService);

  private readonly navigationService: NavigationService = inject(NavigationService);

  private _httpClient: HttpClient = inject(HttpClient);

  protected readonly _kc: KeycloakService = inject(KeycloakService);

  private readonly faIconLibary: FaIconLibrary = inject(FaIconLibrary);

  private readonly locale: Signal<LocaleConfig | undefined> = toSignal(inject(TranslationStoreService).getLocale$());

  private readonly _sidebarService: SidebarService = inject(SidebarService);

  private _currentEntity: CurrentEntity = this.contextService.currenEntity();

  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  collapsed: boolean = this._sidebarService.collapsed();

  constructor () {
    this._translationStoreService
      .getLocale$()
      .pipe(
        takeUntilDestroyed(),
        throttleTime(200, undefined, {
          trailing: true,
          leading: true
        })
      )
      .subscribe(() => this.reloadSidebar());
    this.faIconLibary.addIcons(...SIDEBAR_ICONS);
    effect(() => {
      this.navigationService.urlSegments();

      const current: CurrentEntity = this.contextService.currenEntity();

      if (this._currentEntity?.type === current?.type) {
        this.changeDetectorRef.markForCheck();

        return;
      }

      this._currentEntity = current;
      this.resolveSidebarItems(current).pipe(map((i: ApiSidebarItem[] | null): SidebarItem[] | null => apiToDomain(i, this._kc)))
        .subscribe((items: SidebarItem[] | null): void => {
          this.items.set(items);
        });
    }, { allowSignalWrites: true });
    this._sidebarService.reload$
      .pipe(
        takeUntilDestroyed()
      ).subscribe(() => {
        this.reloadSidebar();
      });
  }

  toggleCollapsed (): void {
    this._sidebarService.toggleCollapsed();
  }

  private resolveSidebarItems (current: CurrentEntity): Observable<ApiSidebarItem[] | null> {
    const localeValue: string = this.locale()?.value ?? "de";

    if (current) {
      switch (current.type) {
        case "customer": {
          return this.getCustomerContent(current.entity.number, localeValue);
        }

        case "project": {
          return this.getProjectContent(current.entity.number);
        }

        case "administration": {
          return this.getAdministrationContent(localeValue);
        }
      }
    } else
      return of(null);
  }


  private reloadSidebar (): void {
    const current: CurrentEntity = this.contextService.currenEntity();

    this._currentEntity = current;
    this.resolveSidebarItems(current).pipe(map((i: ApiSidebarItem[] | null): SidebarItem[] | null => apiToDomain(i, this._kc)))
      .subscribe((items: SidebarItem[] | null): void => {
        this.items.set(items);
      });
  }

  private getProjectContent (projectNumber: string): Observable<ApiSidebarItem[]> {
    const url: string = `api/projects/${ projectNumber }/sidebar-items`;

    return this._httpClient.get<ApiSidebarItem[]>(url);
  }

  // eslint-disable-next-line @tseslint/no-unused-vars
  private getCustomerContent (customerNumber: string, localeValue: string): Observable<ApiSidebarItem[]> {
    const url: string = `public/sidebar-example/customer-sidebar-${ localeValue }.json`;

    return this._httpClient.get<ApiSidebarItem[]>(url);
  }

  private getAdministrationContent (localeValue: string): Observable<ApiSidebarItem[]> {
    const url: string = `public/sidebar-example/administration-sidebar-${ localeValue }.json`;

    return this._httpClient.get<ApiSidebarItem[]>(url);
  }
}
